import { useTheme } from "@emotion/react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { LineChart } from "@mui/x-charts";
import { useWindowSize } from "../../utils";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ReportService from "../../services/reportService";

const MONTH_TICKS = Array.from({ length: 12 }, (_, index) => index + 1);
const getEmptyDataset = () => Array.from({ length: 12 }, (_, index) => ({ month: index + 1, profit: 0 }));

const getLocalizedMonthLabel = (month, t) => {
    const monthNumber = Number(month);
    if (Number.isNaN(monthNumber) || monthNumber < 1 || monthNumber > 12) {
        return String(month);
    }

    const label = t(`monthNames.${monthNumber}`);
    return label.startsWith('monthNames.') ? String(monthNumber) : label;
};

const LineChartBox = () => {
    const theme = useTheme();
    const { width } = useWindowSize();
    const [dataset, setDataset] = useState(getEmptyDataset());
    const [loading, setLoading] = useState(true);
    const { t } = useTranslation();

    useEffect(() => {
        const fetchMonthlyProfit = async () => {
            setLoading(true);
            try {
                const response = await ReportService.fetchMonthlyProfit();

                const months = response?.months || [];
                const filled = getEmptyDataset().map((item) => {
                    const monthData = months.find((m) => m.month === item.month);
                    return {
                        ...item,
                        profit: monthData?.profit ?? 0,
                    };
                });

                setDataset(filled);
            } catch (error) {
                console.error('Error fetching monthly profit:', error);
                setDataset(getEmptyDataset());
            } finally {
                setLoading(false);
            }
        };

        fetchMonthlyProfit();
    }, []);

    return (
        <Box
            sx={{
                bgcolor: 'white',
                boxShadow: 1,
                border: 1,
                borderColor: theme.palette.customGrey.borderInput,
                backgroundColor: 'white',
                borderRadius: 1,
                width: '100%',
                p: 2,
            }}>
            <Typography variant="h7" sx={{ mb: 2, }}>
                {t('monthlyReport')}
            </Typography>
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <LineChart
                    width={Math.max(300, width - 280)}
                    height={320}
                    dataset={dataset}
                    xAxis={[
                        {
                            id: 'Months',
                            dataKey: 'month',
                            scaleType: 'linear',
                            tickInterval: MONTH_TICKS,
                            valueFormatter: (month) => getLocalizedMonthLabel(month, t),
                        },
                    ]}
                    series={[
                        {
                            id: 'Profit',
                            dataKey: 'profit',
                            showMark: true,
                            color: theme.palette.primary.main || '#007bff',
                        },
                    ]}
                />
            )}
        </Box>
    );
};

export default LineChartBox;
